/**
 * M10.8: public "Újdonságok / Változásnapló" (/changelog). No login: it is reachable from the login
 * screen, the game and the profile. Shows only release notes the server published after a successful
 * production deployment — never technical, security or personal details.
 */
import { useEffect, useState } from 'react';
import { APP_NAME } from '../brand.ts';
import { fetchChangelog, type ReleaseNotes } from '../api/updates.ts';

const SECTIONS: Array<[keyof ReleaseNotes['sections'], string, string]> = [
  ['features', 'Új funkciók', 'text-emerald-300'], ['improvements', 'Fejlesztések', 'text-sky-300'], ['fixes', 'Hibajavítások', 'text-amber-300'], ['important', 'Fontos változások', 'text-rose-300'],
];

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' }) : '');

export function ChangelogPage() {
  const [data, setData] = useState<{ current: string; releases: ReleaseNotes[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { fetchChangelog().then(setData).catch(() => setError('A változásnapló most nem érhető el.')); }, []);

  return (
    <div className="w-full h-full overflow-y-auto bg-space-900" style={{ touchAction: 'pan-y' }} data-testid="changelog-page">
      <div className="max-w-2xl mx-auto px-4 py-8 flex flex-col gap-5">
        <header className="flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl font-bold tracking-widest text-accent flex-1 uppercase">{APP_NAME}</h1>
          <a href="/" className="btn-secondary" data-testid="changelog-back">Vissza a játékhoz</a>
        </header>
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Újdonságok / Változásnapló</h2>
          {data && <p className="text-xs text-slate-400 mt-1">Jelenlegi verzió: <span className="font-mono" data-testid="changelog-current">{data.current}</span></p>}
        </div>
        {error && <div className="notice notice-error" role="alert">{error}</div>}
        {!data && !error && <p className="text-sm text-slate-400" role="status">Betöltés…</p>}
        {data && data.releases.length === 0 && (
          <div className="sci-fi-panel rounded-xl p-5 text-center flex flex-col items-center gap-3" data-testid="changelog-empty">
            <p className="text-base font-semibold text-slate-100">Az újdonságok hamarosan itt jelennek meg</p>
            <p className="text-sm text-slate-300 max-w-md">
              Ehhez a verzióhoz ({data.current}) még nincs közzétett változásjegyzék. Amint új verzió érkezik, itt olvashatod, mi változott a játékban.
            </p>
            <a href="/" className="btn-secondary">Vissza a játékhoz</a>
          </div>
        )}
        {data && data.releases.length > 1 && (
          <nav aria-label="Verziók" className="flex flex-wrap gap-2" data-testid="changelog-index">
            {data.releases.map(r => (
              <a key={r.version} href={`#v${r.version}`} className="font-mono text-xs px-3 py-2 min-h-[44px] inline-flex items-center rounded-lg border border-slate-600 text-slate-200 hover:border-accent">v{r.version}</a>
            ))}
          </nav>
        )}
        {data?.releases.map((r, idx) => (
          <article key={r.version} id={`v${r.version}`} className="sci-fi-panel rounded-xl p-4 sm:p-5 scroll-mt-4" data-testid="changelog-release" data-version={r.version}>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="text-lg font-semibold text-slate-100 break-words">{r.title}</h3>
              <span className="font-mono text-xs text-accent">v{r.version}</span>
              {idx === 0 && <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded bg-accent/20 text-accent" data-testid="changelog-latest">Legújabb</span>}
              <span className="text-xs text-slate-400" data-testid="changelog-date">{fmtDate(r.releasedAt)}</span>
            </div>
            {r.summary && <p className="text-sm text-slate-300 mt-2 leading-relaxed">{r.summary}</p>}
            {SECTIONS.map(([key, title, tone]) => r.sections[key].length > 0 && (
              <section key={key} className="mt-4">
                <h4 className={`text-sm font-semibold mb-1.5 ${tone}`}>{title}</h4>
                <ul className="list-disc pl-5 text-sm text-slate-300 flex flex-col gap-1.5 leading-relaxed">
                  {r.sections[key].map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </section>
            ))}
          </article>
        ))}
      </div>
    </div>
  );
}

/** Small link used on the login screen, in the game bar and in the profile. */
export function ChangelogLink({ className = 'btn-link', label = 'Újdonságok / Változásnapló', testId = 'changelog-link' }: { className?: string; label?: string; testId?: string }) {
  return <a href="/changelog" target="_blank" rel="noopener" className={className} data-testid={testId}>{label}</a>;
}
